import React, { createContext, useCallback, useContext, useState } from 'react';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useTranslation } from 'react-i18next';

import { UnsavedForms, UnsavedMessages } from './data';

import type { FC, ReactNode } from 'react';

interface UnsavedItem {
  id: string;
  message?: string;
}

interface IUnsavedChangesContext {
  unsavedItems: Map<string, UnsavedItem>;
  addUnsavedItem: (id: string, message?: string) => void;
  removeUnsavedItem: (id: string) => void;
  hasUnsavedChanges: boolean;
  showUnsavedModal: () => Promise<boolean>;
  UnsavedForms: typeof UnsavedForms;
}

const UnsavedChangesContext = createContext<IUnsavedChangesContext | undefined>(
  undefined,
);

export const useUnsavedChanges = (): IUnsavedChangesContext => {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    return {
      unsavedItems: new Map(),
      addUnsavedItem: () => {},
      removeUnsavedItem: () => {},
      hasUnsavedChanges: false,
      showUnsavedModal: async () => false,
      UnsavedForms,
    };
  }
  return ctx;
};

export const UnsavedChangesProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [unsavedItems, setUnsavedItems] = useState<Map<string, UnsavedItem>>(
    new Map(),
  );

  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();

  const addUnsavedItem = useCallback(
    (id: string, m?: keyof typeof UnsavedMessages | string) => {
      const message = m || UnsavedMessages[id as keyof typeof UnsavedMessages];
      setUnsavedItems((prev) => new Map(prev).set(id, { id, message }));
    },
    [],
  );

  const removeUnsavedItem = useCallback((id: string) => {
    setUnsavedItems((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const showUnsavedModal = useCallback(async () => {
    if (unsavedItems.size === 0) return false;
    const firstItem = unsavedItems.values().next().value;
    const res: boolean = await showAlert({
      title: '',
      successBtnLabel: t('labels.confirm'),
      messageTitle: firstItem?.message ?? t('unsavedMessages.unsavedChanges'),
      closable: false,
    });
    return res;
  }, [unsavedItems]);

  return (
    <UnsavedChangesContext.Provider
      value={{
        unsavedItems,
        addUnsavedItem,
        removeUnsavedItem,
        hasUnsavedChanges: unsavedItems.size > 0,
        showUnsavedModal,
        UnsavedForms,
      }}
    >
      {children}
      {Alert}
    </UnsavedChangesContext.Provider>
  );
};

export default UnsavedChangesProvider;
